import { useQuery } from '@tanstack/react-query';
import { FolderLock, ShieldCheck } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { getSecureStorageConfig } from '@/lib/api';
import type { SecureStorageConfig } from '@/lib/api';

export function SecureStorageSection(): React.ReactElement {
  const { data, isLoading } = useQuery<SecureStorageConfig>({
    queryKey: ['secureStorageConfig'],
    queryFn: getSecureStorageConfig,
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <div className="flex items-center gap-2">
            <FolderLock className="size-5 text-text-secondary" />
            <span>Secure Storage</span>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="text-sm text-muted-foreground">Loading...</div>
        ) : !data ? (
          <div className="text-sm text-muted-foreground">Unable to load vault configuration.</div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-text-secondary">Vault path</span>
              <code className="rounded bg-surface px-2 py-0.5 font-mono text-xs text-text-primary border border-border">
                {data.path}
              </code>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-text-secondary">Status</span>
              {data.writable ? (
                <Badge className="bg-success/20 text-success border-success/30">
                  <ShieldCheck className="size-3" />
                  Writable
                </Badge>
              ) : (
                <Badge className="bg-warning/20 text-warning border-warning/30">
                  Not initialized
                </Badge>
              )}
            </div>
            <p className="text-xs text-text-secondary pt-1">
              API keys detected in projects are encrypted and stored at this location. Configure via{' '}
              <code className="font-mono text-text-primary">ARCHON_VAULT_PATH</code> environment
              variable.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
